import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, type DrawerProps, Form, Space } from 'antd';
import { FieldArray, useField } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormFields from '../../../themes/admin/Fields/FormFields';
import DrawerWithAutoWidth from '../../DrawerWithAutoWidth';
// eslint-disable-next-line max-len
import { type SelectFieldProps } from '../SelectField';

type PriceDisclaimerMultipleEntryDrawerProps = Pick<DrawerProps, 'title' | 'visible' | 'onClose'> &
    Pick<SelectFieldProps, 'options'> & {
        name: string;
    };

const PriceDisclaimerMultipleEntryDrawer = ({
    onClose,
    title,
    visible,
    name,
}: PriceDisclaimerMultipleEntryDrawerProps) => {
    const { t } = useTranslation(['translationField', 'dealershipField']);
    const [field] = useField(name);

    return (
        <DrawerWithAutoWidth onClose={onClose} placement="right" title={title} visible={visible} destroyOnClose>
            <FieldArray
                {...field}
                name={name}
                render={arrayHelpers => (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Form layout="vertical">
                            {field.value.map((value, index) => (
                                <FormFields.TranslatedInputField
                                    addMoreButton={
                                        <Button
                                            icon={<DeleteOutlined />}
                                            onClick={() => {
                                                arrayHelpers.remove(index);
                                            }}
                                            size="small"
                                            type="text"
                                        />
                                    }
                                    name={`${name}.${index}`}
                                />
                            ))}
                        </Form>
                        <Button
                            icon={<PlusOutlined />}
                            onClick={() => {
                                arrayHelpers.push({ defaultValue: '', overrides: [] });
                            }}
                            type="dashed"
                            block
                        >
                            {t('dealershipField:priceDisclaimer.addButton')}
                        </Button>
                    </Space>
                )}
            />
        </DrawerWithAutoWidth>
    );
};

export default PriceDisclaimerMultipleEntryDrawer;

export const usePriceDisclaimerDrawer = () => {
    const [visible, setVisible] = useState(false);

    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    return {
        ...actions,
        render: (props: Pick<PriceDisclaimerMultipleEntryDrawerProps, 'name' | 'title' | 'options'>) => (
            <PriceDisclaimerMultipleEntryDrawer {...props} onClose={actions.close} visible={visible} />
        ),
    };
};
